import type { Game } from "./types.js";

interface Vec {
  x: number;
  y: number;
}

interface ShipPoints {
  nose: Vec;
  left: Vec;
  right: Vec;
  tail: Vec;
}

interface Bullet {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  life: number;
  from: "ship" | "ufo";
}

interface AsteroidVert {
  a: number;
  r: number;
}

interface Asteroid {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  size: 1 | 2 | 3;
  a: number;
  av: number;
  verts: AsteroidVert[];
}

interface Ufo {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  big: boolean;
  shootTimer: number;
  turnTimer: number;
  life: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  max: number;
  color: string;
}

interface Star {
  x: number;
  y: number;
  b: number;
}

type GameState = "title" | "play" | "paused" | "gameover";

const STROKE = "#22d3ee";
const TEXT = "#ececf1";
const TEXT_DIM = "rgba(236, 236, 241, 0.7)";
const THRUST_COLOR = "#ffb070";
const BG = "#0a0a0f";

let wrap: HTMLDivElement | null = null;
let cleanupFn: (() => void) | null = null;

function buildDom(host: HTMLElement): {
  wrap: HTMLDivElement;
  canvas: HTMLCanvasElement;
  overlayEl: HTMLDivElement;
  startBtn: HTMLButtonElement;
} {
  const wrapEl = document.createElement("div");
  wrapEl.className = "asteroids-wrap";

  const canvas = document.createElement("canvas");
  canvas.width = 1000;
  canvas.height = 750;
  canvas.className = "asteroids-canvas";
  canvas.setAttribute("aria-hidden", "true");

  const overlayEl = document.createElement("div");
  overlayEl.className = "asteroids-overlay";
  overlayEl.innerHTML = `
    <h1 class="asteroids-overlay__title">ASTEROIDS</h1>
    <p><kbd>&larr;</kbd> <kbd>&rarr;</kbd> rotate &nbsp; <kbd>&uarr;</kbd> thrust</p>
    <p><kbd>Space</kbd> fire &nbsp; <kbd>H</kbd> hyperspace &nbsp; <kbd>P</kbd> pause</p>
    <button class="asteroids-overlay__btn" type="button">START</button>
  `;

  const startBtn = overlayEl.querySelector<HTMLButtonElement>(".asteroids-overlay__btn")!;

  wrapEl.appendChild(canvas);
  wrapEl.appendChild(overlayEl);
  host.appendChild(wrapEl);

  return { wrap: wrapEl, canvas, overlayEl, startBtn };
}

function mount(host: HTMLElement): void {
  const dom = buildDom(host);
  wrap = dom.wrap;
  const { canvas, overlayEl, startBtn } = dom;
  const ctx = canvas.getContext("2d");
  if (!ctx) return;

  const W = canvas.width;
  const H = canvas.height;
  const TAU = Math.PI * 2;
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const rand = (a: number, b: number): number => a + Math.random() * (b - a);
  const randi = (a: number, b: number): number => Math.floor(rand(a, b));
  const wrapPos = (p: { x: number; y: number; r: number }): void => {
    if (p.x < -p.r) p.x = W + p.r;
    else if (p.x > W + p.r) p.x = -p.r;
    if (p.y < -p.r) p.y = H + p.r;
    else if (p.y > H + p.r) p.y = -p.r;
  };
  const dist2 = (a: Vec, b: Vec): number => {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    return dx * dx + dy * dy;
  };
  const circleHit = (a: { x: number; y: number; r: number }, b: { x: number; y: number; r: number }): boolean =>
    dist2(a, b) < (a.r + b.r) * (a.r + b.r);

  const keys: Record<string, boolean> = {};
  const keyMap: Record<string, string> = {
    ArrowLeft: "left", KeyA: "left",
    ArrowRight: "right", KeyD: "right",
    ArrowUp: "thrust", KeyW: "thrust",
    Space: "fire",
    KeyH: "hyper",
    KeyP: "pause",
  };

  const SHIP_R = 14;
  const ship = {
    x: W / 2, y: H / 2,
    vx: 0, vy: 0,
    a: -Math.PI / 2,
    r: SHIP_R,
    thrusting: false,
    invuln: 0,
    alive: true,
    dead: 0,
    lives: 3,
    flicker: 0,
  };

  const bullets: Bullet[] = [];
  const asteroids: Asteroid[] = [];
  const particles: Particle[] = [];
  let ufo: Ufo | null = null;
  let ufoTimer = 12;

  let score = 0;
  let highScore = parseInt(localStorage.getItem("asteroids_hi") ?? "0", 10) || 0;
  let level = 1;
  let nextExtraAt = 10000;
  let state: GameState = "title";
  let levelMsg = "";
  let levelMsgTimer = 0;
  let asteroidsCleared = false;

  let raf: number | null = null;
  const timers: Set<number> = new Set();

  const setTimer = (fn: () => void, ms: number): void => {
    const id = window.setTimeout(() => {
      timers.delete(id);
      fn();
    }, ms);
    timers.add(id);
  };

  const stars: Star[] = [];
  for (let i = 0; i < 80; i++) {
    stars.push({ x: Math.random() * W, y: Math.random() * H, b: Math.random() });
  }

  function resetShip(invuln = 2.5): void {
    ship.x = W / 2; ship.y = H / 2;
    ship.vx = 0; ship.vy = 0;
    ship.a = -Math.PI / 2;
    ship.alive = true; ship.dead = 0;
    ship.invuln = invuln;
    ship.flicker = 0;
  }

  function shipPoints(): ShipPoints {
    const c = Math.cos(ship.a);
    const s = Math.sin(ship.a);
    const nose = { x: ship.x + c * SHIP_R, y: ship.y + s * SHIP_R };
    const back = ship.a + Math.PI;
    const left = { x: ship.x + Math.cos(back - 0.45) * SHIP_R, y: ship.y + Math.sin(back - 0.45) * SHIP_R };
    const right = { x: ship.x + Math.cos(back + 0.45) * SHIP_R, y: ship.y + Math.sin(back + 0.45) * SHIP_R };
    const tail = { x: ship.x + Math.cos(back) * SHIP_R * 0.55, y: ship.y + Math.sin(back) * SHIP_R * 0.55 };
    return { nose, left, right, tail };
  }

  function spawnParticles(x: number, y: number, n: number, speed = 120, life = 0.7, color = TEXT): void {
    if (reduced) return;
    for (let i = 0; i < n; i++) {
      const a = Math.random() * TAU;
      const s = rand(0.3, 1) * speed;
      particles.push({
        x, y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: rand(0.4, 1) * life,
        max: rand(0.4, 1) * life,
        color,
      });
    }
  }

  function makeAsteroid(x: number, y: number, size: 1 | 2 | 3, vxIn?: number, vyIn?: number): Asteroid {
    const r = size === 3 ? 44 : size === 2 ? 24 : 12;
    const speedRange = size === 3 ? [30, 90] : size === 2 ? [60, 140] : [100, 200];
    const speed = rand(speedRange[0]!, speedRange[1]!);
    const ang = Math.random() * TAU;
    const vx = vxIn ?? Math.cos(ang) * speed;
    const vy = vyIn ?? Math.sin(ang) * speed;
    const n = randi(10, 14);
    const verts: AsteroidVert[] = [];
    for (let i = 0; i < n; i++) {
      const a = (i / n) * TAU;
      const rr = r * rand(0.78, 1.18);
      verts.push({ a, r: rr });
    }
    return { x, y, vx, vy, r, size, a: 0, av: rand(-1.2, 1.2), verts };
  }

  function splitAsteroid(idx: number): void {
    const a = asteroids[idx]!;
    asteroids.splice(idx, 1);
    if (a.size === 3) {
      spawnParticles(a.x, a.y, 18, 140, 0.7, STROKE);
      for (let i = 0; i < 2; i++) asteroids.push(makeAsteroid(a.x, a.y, 2));
      score += 20;
    } else if (a.size === 2) {
      spawnParticles(a.x, a.y, 14, 160, 0.6, STROKE);
      for (let i = 0; i < 2; i++) asteroids.push(makeAsteroid(a.x, a.y, 1));
      score += 50;
    } else {
      spawnParticles(a.x, a.y, 10, 180, 0.5, STROKE);
      score += 100;
    }
    if (score > highScore) highScore = score;
    checkExtraLife();
  }

  function spawnUfo(): void {
    const big = level < 3 ? Math.random() < 0.5 : Math.random() < 0.3;
    const fromLeft = Math.random() < 0.5;
    const speed = big ? 90 : 130;
    ufo = {
      x: fromLeft ? -30 : W + 30,
      y: rand(60, H - 60),
      vx: fromLeft ? speed : -speed,
      vy: 0,
      r: big ? 18 : 11,
      big,
      shootTimer: rand(0.6, 1.2),
      turnTimer: rand(1.0, 2.0),
      life: 14,
    };
  }

  function killShip(): void {
    if (!ship.alive || ship.invuln > 0) return;
    ship.alive = false; ship.dead = 0;
    spawnParticles(ship.x, ship.y, 28, 180, 1.0, TEXT);
  }

  function checkExtraLife(): void {
    if (score >= nextExtraAt) {
      ship.lives++;
      nextExtraAt += 10000;
    }
  }

  function startLevel(n: number): void {
    level = n;
    asteroids.length = 0;
    bullets.length = 0;
    ufo = null;
    ufoTimer = rand(14, 22);
    const count = Math.min(4 + n, 11);
    for (let i = 0; i < count; i++) {
      let x: number, y: number;
      do {
        x = rand(0, W); y = rand(0, H);
      } while (Math.hypot(x - ship.x, y - ship.y) < 180);
      asteroids.push(makeAsteroid(x, y, 3));
    }
    levelMsg = "WAVE " + n;
    levelMsgTimer = 1.6;
    asteroidsCleared = false;
  }

  function newGame(): void {
    score = 0;
    ship.lives = 3;
    nextExtraAt = 10000;
    particles.length = 0;
    resetShip(2.0);
    startLevel(1);
    state = "play";
    overlayEl.classList.add("hidden");
  }

  function gameOver(): void {
    state = "gameover";
    if (score > highScore) {
      highScore = score;
    }
    try { localStorage.setItem("asteroids_hi", String(highScore)); } catch { /* ignore */ }
    showOverlay("GAME OVER", `Score <b>${score}</b> &nbsp; High <b>${highScore}</b>`, "PLAY AGAIN");
  }

  function showOverlay(title: string, sub: string, btnText = "START"): void {
    overlayEl.classList.remove("hidden");
    const h1 = overlayEl.querySelector("h1")!;
    h1.textContent = title;
    const ps = overlayEl.querySelectorAll("p");
    ps[0]!.innerHTML = sub;
    ps[1]!.innerHTML = "&nbsp;";
    startBtn.textContent = btnText;
  }

  function updateShip(dt: number): void {
    if (!ship.alive) {
      ship.dead += dt;
      return;
    }
    if (ship.invuln > 0) ship.invuln -= dt;

    if (keys.left) ship.a -= 3.6 * dt;
    if (keys.right) ship.a += 3.6 * dt;

    ship.thrusting = !!keys.thrust;
    if (ship.thrusting) {
      ship.vx += Math.cos(ship.a) * 280 * dt;
      ship.vy += Math.sin(ship.a) * 280 * dt;
      if (!reduced) {
        const back = ship.a + Math.PI;
        particles.push({
          x: ship.x + Math.cos(back) * SHIP_R,
          y: ship.y + Math.sin(back) * SHIP_R,
          vx: Math.cos(back) * 80 + rand(-30, 30) - ship.vx * 0.2,
          vy: Math.sin(back) * 80 + rand(-30, 30) - ship.vy * 0.2,
          life: 0.3, max: 0.3, color: THRUST_COLOR,
        });
      }
    }

    const maxV = 420;
    const sp = Math.hypot(ship.vx, ship.vy);
    if (sp > maxV) { ship.vx *= maxV / sp; ship.vy *= maxV / sp; }
    ship.vx *= Math.pow(0.995, dt * 60);
    ship.vy *= Math.pow(0.995, dt * 60);

    ship.x += ship.vx * dt;
    ship.y += ship.vy * dt;
    wrapPos(ship);

    if (keys.firePressed && bullets.length < 5) {
      const c = Math.cos(ship.a);
      const s = Math.sin(ship.a);
      bullets.push({
        x: ship.x + c * SHIP_R,
        y: ship.y + s * SHIP_R,
        vx: ship.vx + c * 560,
        vy: ship.vy + s * 560,
        r: 1.5,
        life: 0.85,
        from: "ship",
      });
    }
    keys.firePressed = false;

    if (keys.hyperPressed) {
      ship.x = rand(40, W - 40);
      ship.y = rand(40, H - 40);
      ship.vx = 0; ship.vy = 0;
      // 1-in-32 chance of misjump (kinder than the original 1-in-16)
      if (Math.random() < 1 / 32) killShip();
      else ship.invuln = 1.2;
    }
    keys.hyperPressed = false;
  }

  function updateBullets(dt: number): void {
    for (let i = bullets.length - 1; i >= 0; i--) {
      const b = bullets[i]!;
      b.x += b.vx * dt; b.y += b.vy * dt;
      b.life -= dt;
      wrapPos(b);
      if (b.life <= 0) bullets.splice(i, 1);
    }
  }

  function updateAsteroids(dt: number): void {
    for (const a of asteroids) {
      a.x += a.vx * dt; a.y += a.vy * dt; a.a += a.av * dt;
      wrapPos(a);
    }
  }

  function updateUfo(dt: number): void {
    if (!ufo) {
      ufoTimer -= dt;
      if (ufoTimer <= 0 && asteroids.length > 0 && ship.alive) {
        spawnUfo();
        ufoTimer = rand(15, 28);
      }
      return;
    }
    ufo.x += ufo.vx * dt; ufo.y += ufo.vy * dt;
    ufo.life -= dt;
    ufo.turnTimer -= dt;
    if (ufo.turnTimer <= 0) {
      ufo.vy = rand(-60, 60);
      ufo.turnTimer = rand(0.8, 2.0);
    }
    if (ufo.y < 30) ufo.y = 30;
    if (ufo.y > H - 30) ufo.y = H - 30;

    ufo.shootTimer -= dt;
    if (ufo.shootTimer <= 0 && ship.alive) {
      let ang: number;
      if (ufo.big) {
        ang = Math.random() * TAU;
      } else {
        const accuracy = Math.min(0.25, 0.04 + score / 40000);
        ang = Math.atan2(ship.y - ufo.y, ship.x - ufo.x) + rand(-accuracy, accuracy) * Math.PI;
      }
      const bspeed = 360;
      bullets.push({
        x: ufo.x, y: ufo.y,
        vx: Math.cos(ang) * bspeed,
        vy: Math.sin(ang) * bspeed,
        r: 1.5, life: 1.0, from: "ufo",
      });
      ufo.shootTimer = rand(0.9, 1.7);
    }

    if (ufo.life <= 0 || ufo.x < -50 || ufo.x > W + 50) ufo = null;
  }

  function updateParticles(dt: number): void {
    for (let i = particles.length - 1; i >= 0; i--) {
      const p = particles[i]!;
      p.x += p.vx * dt; p.y += p.vy * dt;
      p.life -= dt;
      if (p.x < 0) p.x += W; else if (p.x > W) p.x -= W;
      if (p.y < 0) p.y += H; else if (p.y > H) p.y -= H;
      if (p.life <= 0) particles.splice(i, 1);
    }
  }

  function handleCollisions(): void {
    for (let i = bullets.length - 1; i >= 0; i--) {
      const b = bullets[i]!;
      let hit = false;
      for (let j = asteroids.length - 1; j >= 0; j--) {
        if (circleHit(b, asteroids[j]!)) {
          bullets.splice(i, 1);
          splitAsteroid(j);
          hit = true;
          break;
        }
      }
      if (hit) continue;
      if (ufo && b.from === "ship" && circleHit(b, ufo)) {
        bullets.splice(i, 1);
        spawnParticles(ufo.x, ufo.y, 18, 200, 0.8, STROKE);
        score += ufo.big ? 200 : 1000;
        if (score > highScore) highScore = score;
        ufo = null;
        checkExtraLife();
      }
    }
    for (let i = bullets.length - 1; i >= 0; i--) {
      const b = bullets[i];
      if (!b || b.from !== "ufo") continue;
      for (let j = asteroids.length - 1; j >= 0; j--) {
        if (circleHit(b, asteroids[j]!)) {
          bullets.splice(i, 1);
          splitAsteroid(j);
          break;
        }
      }
    }
    if (ship.alive && ship.invuln <= 0) {
      for (let j = asteroids.length - 1; j >= 0; j--) {
        if (circleHit(ship, asteroids[j]!)) {
          splitAsteroid(j);
          killShip();
          return;
        }
      }
      if (ufo && circleHit(ship, ufo)) {
        spawnParticles(ufo.x, ufo.y, 18, 200, 0.8, STROKE);
        ufo = null;
        killShip();
        return;
      }
      for (let i = bullets.length - 1; i >= 0; i--) {
        const b = bullets[i]!;
        if (b.from === "ufo" && circleHit(ship, b)) {
          bullets.splice(i, 1);
          killShip();
          return;
        }
      }
    }
  }

  function drawBackground(): void {
    ctx!.fillStyle = BG;
    ctx!.fillRect(0, 0, W, H);
    ctx!.strokeStyle = "rgba(139, 92, 246, 0.07)";
    ctx!.lineWidth = 1;
    for (let x = 0; x < W; x += 40) {
      ctx!.beginPath();
      ctx!.moveTo(x, 0); ctx!.lineTo(x, H);
      ctx!.stroke();
    }
    for (let y = 0; y < H; y += 40) {
      ctx!.beginPath();
      ctx!.moveTo(0, y); ctx!.lineTo(W, y);
      ctx!.stroke();
    }
    for (const s of stars) {
      ctx!.globalAlpha = 0.12 + s.b * 0.25;
      ctx!.fillStyle = TEXT;
      ctx!.fillRect(s.x, s.y, 1, 1);
    }
    ctx!.globalAlpha = 1;
  }

  function drawShip(): void {
    if (!ship.alive) return;
    if (ship.invuln > 0) {
      ship.flicker += 1;
      if (Math.floor(ship.flicker / 4) % 2 === 0) return;
    }
    const p = shipPoints();
    ctx!.strokeStyle = STROKE;
    ctx!.lineWidth = 2;
    ctx!.lineJoin = "round";
    ctx!.lineCap = "round";
    ctx!.beginPath();
    ctx!.moveTo(p.nose.x, p.nose.y);
    ctx!.lineTo(p.left.x, p.left.y);
    ctx!.lineTo(p.tail.x, p.tail.y);
    ctx!.lineTo(p.right.x, p.right.y);
    ctx!.closePath();
    ctx!.stroke();

    if (ship.thrusting && Math.random() < 0.7) {
      const back = ship.a + Math.PI;
      const fx1 = { x: ship.x + Math.cos(back - 0.25) * SHIP_R * 0.7, y: ship.y + Math.sin(back - 0.25) * SHIP_R * 0.7 };
      const fx2 = { x: ship.x + Math.cos(back + 0.25) * SHIP_R * 0.7, y: ship.y + Math.sin(back + 0.25) * SHIP_R * 0.7 };
      const fx3 = { x: ship.x + Math.cos(back) * SHIP_R * (1.1 + Math.random() * 0.5),
                    y: ship.y + Math.sin(back) * SHIP_R * (1.1 + Math.random() * 0.5) };
      ctx!.strokeStyle = THRUST_COLOR;
      ctx!.beginPath();
      ctx!.moveTo(fx1.x, fx1.y); ctx!.lineTo(fx3.x, fx3.y); ctx!.lineTo(fx2.x, fx2.y);
      ctx!.stroke();
    }
  }

  function drawAsteroid(a: Asteroid): void {
    ctx!.strokeStyle = STROKE;
    ctx!.lineWidth = 2;
    ctx!.lineJoin = "round";
    const drawAt = (cx: number, cy: number): void => {
      ctx!.beginPath();
      for (let i = 0; i < a.verts.length; i++) {
        const v = a.verts[i]!;
        const ang = v.a + a.a;
        const px = cx + Math.cos(ang) * v.r;
        const py = cy + Math.sin(ang) * v.r;
        if (i === 0) ctx!.moveTo(px, py); else ctx!.lineTo(px, py);
      }
      ctx!.closePath();
      ctx!.stroke();
    };
    drawAt(a.x, a.y);
    if (a.x < a.r) drawAt(a.x + W, a.y);
    else if (a.x > W - a.r) drawAt(a.x - W, a.y);
    if (a.y < a.r) drawAt(a.x, a.y + H);
    else if (a.y > H - a.r) drawAt(a.x, a.y - H);
  }

  function drawUfo(): void {
    if (!ufo) return;
    const r = ufo.r;
    ctx!.strokeStyle = STROKE;
    ctx!.lineWidth = 2;
    ctx!.beginPath();
    ctx!.moveTo(ufo.x - r, ufo.y);
    ctx!.lineTo(ufo.x - r * 0.5, ufo.y - r * 0.45);
    ctx!.lineTo(ufo.x + r * 0.5, ufo.y - r * 0.45);
    ctx!.lineTo(ufo.x + r, ufo.y);
    ctx!.lineTo(ufo.x + r * 0.5, ufo.y + r * 0.45);
    ctx!.lineTo(ufo.x - r * 0.5, ufo.y + r * 0.45);
    ctx!.closePath();
    ctx!.stroke();
    ctx!.beginPath();
    ctx!.moveTo(ufo.x - r * 0.35, ufo.y - r * 0.45);
    ctx!.quadraticCurveTo(ufo.x, ufo.y - r * 1.1, ufo.x + r * 0.35, ufo.y - r * 0.45);
    ctx!.stroke();
    ctx!.beginPath();
    ctx!.moveTo(ufo.x - r, ufo.y);
    ctx!.lineTo(ufo.x + r, ufo.y);
    ctx!.stroke();
  }

  function drawBullets(): void {
    ctx!.fillStyle = TEXT;
    for (const b of bullets) {
      ctx!.fillRect(b.x - 1.5, b.y - 1.5, 3, 3);
    }
  }

  function drawParticles(): void {
    for (const p of particles) {
      const a = Math.max(0, p.life / p.max);
      ctx!.globalAlpha = a;
      ctx!.fillStyle = p.color;
      ctx!.fillRect(p.x - 1, p.y - 1, 2, 2);
    }
    ctx!.globalAlpha = 1;
  }

  function drawLivesIcon(x: number, y: number): void {
    ctx!.strokeStyle = STROKE;
    ctx!.lineWidth = 2;
    ctx!.beginPath();
    const r = 9;
    ctx!.moveTo(x, y - r);
    ctx!.lineTo(x - r * 0.7, y + r * 0.7);
    ctx!.lineTo(x, y + r * 0.35);
    ctx!.lineTo(x + r * 0.7, y + r * 0.7);
    ctx!.closePath();
    ctx!.stroke();
  }

  function drawHUD(): void {
    ctx!.fillStyle = TEXT;
    ctx!.font = '700 16px "IBM Plex Mono", ui-monospace, monospace';
    ctx!.textAlign = "left";
    ctx!.textBaseline = "alphabetic";
    ctx!.fillText(`SCORE  ${score.toString().padStart(5, "0")}`, 16, 28);
    ctx!.textAlign = "center";
    ctx!.fillText(`WAVE ${level}`, W / 2, 28);
    ctx!.textAlign = "right";
    ctx!.fillStyle = TEXT_DIM;
    ctx!.fillText(`HI ${highScore.toString().padStart(5, "0")}`, W - 16, 28);

    for (let i = 0; i < ship.lives; i++) {
      drawLivesIcon(24 + i * 22, 56);
    }

    if (levelMsgTimer > 0) {
      ctx!.globalAlpha = Math.min(1, levelMsgTimer);
      ctx!.fillStyle = TEXT;
      ctx!.font = '700 48px "IBM Plex Mono", ui-monospace, monospace';
      ctx!.textAlign = "center";
      ctx!.fillText(levelMsg, W / 2, H / 2 - 40);
      ctx!.globalAlpha = 1;
    }

    if (state === "paused") {
      ctx!.fillStyle = "rgba(10, 10, 15, 0.7)";
      ctx!.fillRect(0, 0, W, H);
      ctx!.textAlign = "center";
      ctx!.fillStyle = TEXT;
      ctx!.font = '700 48px "IBM Plex Mono", ui-monospace, monospace';
      ctx!.fillText("PAUSED", W / 2, H / 2);
      ctx!.font = '400 14px "IBM Plex Mono", ui-monospace, monospace';
      ctx!.fillStyle = "#a0a0b0";
      ctx!.fillText("Press P to resume", W / 2, H / 2 + 30);
    }
  }

  function update(dt: number): void {
    if (state === "paused") return;

    if (state === "title") {
      updateAsteroids(dt);
      return;
    }

    if (state === "gameover") {
      updateAsteroids(dt);
      updateUfo(dt);
      updateParticles(dt);
      updateBullets(dt);
      return;
    }

    updateShip(dt);
    updateBullets(dt);
    updateAsteroids(dt);
    updateUfo(dt);
    updateParticles(dt);
    handleCollisions();

    if (!ship.alive && ship.dead > 1.6) {
      ship.lives--;
      if (ship.lives <= 0) {
        gameOver();
      } else {
        let safe = true;
        for (const a of asteroids) {
          if (Math.hypot(a.x - W / 2, a.y - H / 2) < 130) { safe = false; break; }
        }
        if (ufo) safe = false;
        if (safe) resetShip(2.0);
        else ship.dead = 1.4;
      }
    }
    if (asteroids.length === 0 && !asteroidsCleared) {
      asteroidsCleared = true;
      setTimer(() => {
        if (state === "play") startLevel(level + 1);
      }, 1400);
    }
    if (levelMsgTimer > 0) levelMsgTimer -= dt;
  }

  function draw(): void {
    ctx!.clearRect(0, 0, W, H);
    drawBackground();
    for (const a of asteroids) drawAsteroid(a);
    drawUfo();
    drawBullets();
    drawShip();
    drawParticles();
    drawHUD();
  }

  let last = performance.now();
  function loop(now: number): void {
    let dt = (now - last) / 1000;
    last = now;
    if (dt > 0.05) dt = 0.05;
    update(dt);
    draw();
    raf = requestAnimationFrame(loop);
  }

  const onKeyDown = (e: KeyboardEvent): void => {
    const k = keyMap[e.code];
    if (!k) return;
    if (e.repeat) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    if (!keys[k]) keys[k + "Pressed"] = true;
    keys[k] = true;

    if (k === "pause") {
      if (state === "play") state = "paused";
      else if (state === "paused") state = "play";
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    const k = keyMap[e.code];
    if (!k) return;
    keys[k] = false;
  };
  const onStartClick = (): void => {
    newGame();
  };

  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  startBtn.addEventListener("click", onStartClick);

  // Pre-populate the title screen background with some drifting asteroids
  for (let i = 0; i < 6; i++) asteroids.push(makeAsteroid(rand(0, W), rand(0, H), 3));

  raf = requestAnimationFrame((t) => {
    last = t;
    loop(t);
  });

  cleanupFn = (): void => {
    if (raf !== null) cancelAnimationFrame(raf);
    raf = null;
    for (const id of timers) clearTimeout(id);
    timers.clear();
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
    startBtn.removeEventListener("click", onStartClick);
  };
}

function unmount(): void {
  if (cleanupFn) cleanupFn();
  cleanupFn = null;
  if (wrap) wrap.remove();
  wrap = null;
}

export const asteroids: Game = {
  id: "asteroids",
  title: "Asteroids",
  description: "Vector-shooter classic. Arrows, space, H for hyperspace.",
  mount,
  unmount,
};
